// DashboardHeader.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './DashboardHeader.css';

// Components
import IconButton from 'components/iconbutton/IconButton';
import IconPickerButton from 'components/iconpicker/IconPickerButton';
import SectionHeader from 'components/section/header/SectionHeader';
import DashboardTitle from 'dashboard/title/DashboardTitle';

/**
 * DashboardHeader
 *
 * @param {boolean} [hideIdentity=false] - Hide icon picker + title (e.g. GlobalToolbar).
 * @param {boolean} [hideActions=false] - Hide Edit / Pin / Settings / Delete (moved to footer).
 * @returns {ReactNode} The rendered component.
 */
const DashboardHeader = ({
    showHeader,
    theme,
    dashboard,
    dashboardIconKey,
    isPinnedToHome,
    isLayoutEditMode = false,
    hideIdentity = false,
    hideActions = false,
    handleSelectDashboardIcon,
    handleDashboardTitleSaved,
    handleTogglePinToHome,
    handleToggleLayoutEditMode = null,
    handleOpenSettingsModal,
    handleDeleteDashboard,
    showLayoutEditToggle = false,
}) => {

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();
    const pinActionLabel = isPinnedToHome ? 'Unpin from home screen' : 'Pin to home screen';
    if (!showHeader) return null;

    const showIdentity = !hideIdentity;
    const showActions = !hideActions;

    if (!showIdentity && !showActions) {
        return null;
    }

    // ----- RENDER ----- //

    return (

        <SectionHeader>

            {/* ICON WRAPPER */}
            {showIdentity && !isMobile && (

                <div className="dashboard-header-icon-wrapper">
                    <IconPickerButton
                      backgroundColor="transparent"
                      borderRadius={8}
                      buttonSize={46}
                      disabled={!dashboard?.key}
                      hoverBackgroundColor={theme.backgroundHover}
                      iconColor={theme.foreground}
                      iconSize={30}
                      popupHeight={360}
                      popupWidth={380}
                      selectedIconKey={dashboardIconKey}
                      title="Choose dashboard icon"
                      onSelect={handleSelectDashboardIcon}
                    />
                </div>
            )}

            {/* TITLE WRAPPER */}
            {showIdentity ? (
                <div className="dashboard-header-title-wrapper">
                    <DashboardTitle
                      dashboard={dashboard}
                      onTitleSaved={handleDashboardTitleSaved}
                    />
                </div>
            ) : null}

            {/* BUTTONS */}
            {showActions ? (
                <div className="dashboard-header-buttons">

                    {showLayoutEditToggle ? (
                        <IconButton
                          iconKey={isLayoutEditMode ? 'check' : 'edit'}
                          iconSize={20}
                          title={isLayoutEditMode ? 'Done' : 'Edit Layout'}
                          onClick={handleToggleLayoutEditMode}
                        />
                    ) : null}

                    <IconButton
                      iconKey="keep"
                      iconSize={20}
                      title={pinActionLabel}
                      color={isPinnedToHome ? theme.foreground : theme.foregroundMuted}
                      onClick={handleTogglePinToHome}
                    />

                    <IconButton
                      iconKey="settings"
                      iconSize={20}
                      title="Dashboard settings"
                      onClick={handleOpenSettingsModal}
                    />

                    <IconButton
                      iconKey="delete"
                      iconSize={20}
                      title="Delete dashboard"
                      onClick={handleDeleteDashboard}
                    />

                </div>
            ) : null}

        </SectionHeader>
    );
};

// Dev Tools
DashboardHeader.displayName = 'DashboardHeader';

export default React.memo(DashboardHeader);
